/**
 * Map bidding-agent timber extraction onto the TimberListItem shape
 * proposalBuilders.nodeSets[].materials.timberList expects.
 */

import type {
  BiddingTakeoffStructure,
  BiddingTimberExtraction,
  BiddingTimberItem,
  LeadBiddingAnalysis,
  PdfTakeoffStructure,
  TimberListItem,
} from '../types';

/** Keep this file firebase-free so unit tests do not load Auth. */

function standardizeTimberDimensions(dimensions: string): string {
  const s = (dimensions || '').trim();
  const match = s.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
  if (!match) return s;
  const a = parseFloat(match[1]);
  const b = parseFloat(match[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return s;
  if (a <= b) return `${match[1]}x${match[2]}x${match[3]}`;
  return `${match[2]}x${match[1]}x${match[3]}`;
}

function parseBoardFeet(dimensions: string): number {
  const s = standardizeTimberDimensions(dimensions || '');
  const match = s.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
  if (!match) return 0;
  return (parseFloat(match[1]) * parseFloat(match[2]) * parseFloat(match[3])) / 12;
}

function defaultTimberOrderLengthFt(measuredFt: number): number {
  const ceiled = Math.ceil(measuredFt);
  const evenCeiled = ceiled % 2 === 0 ? ceiled : ceiled + 1;
  return Math.max(evenCeiled, 8);
}

/** Feet from agent length strings: `12'-6"`, `12'`, `12`, `12.5`. */
export function parseLengthFeet(raw: string | number | undefined | null): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  const s = String(raw ?? '').trim();
  if (!s) return null;

  const feetInches = s.match(/^(\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)?\s*"?\s*$/);
  if (feetInches) {
    const feet = parseFloat(feetInches[1]);
    const inches = feetInches[2] ? parseFloat(feetInches[2]) : 0;
    if (!Number.isFinite(feet)) return null;
    const total = feet + (Number.isFinite(inches) ? inches / 12 : 0);
    return total > 0 ? total : null;
  }

  const feetOnly = s.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft|feet)?\s*$/i);
  if (feetOnly) {
    const feet = parseFloat(feetOnly[1]);
    return Number.isFinite(feet) && feet > 0 ? feet : null;
  }

  return null;
}

export function dimensionsFromBiddingItem(item: BiddingTimberItem): string {
  const existing = (item.dimensions || '').trim();
  if (existing && /(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/.test(existing)) {
    return standardizeTimberDimensions(existing);
  }

  const width = Number(item.width);
  const height = Number(item.height);
  const measuredFt = parseLengthFeet(item.length);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0 || measuredFt == null) {
    return existing;
  }
  const orderFt = defaultTimberOrderLengthFt(measuredFt);
  return standardizeTimberDimensions(`${height}x${width}x${orderFt}`);
}

export function timberListItemFromBiddingItem(item: BiddingTimberItem): TimberListItem {
  const dimensions = dimensionsFromBiddingItem(item);
  const unitCost = typeof item.unitCost === 'number' && Number.isFinite(item.unitCost) ? item.unitCost : 0;
  const quantity = Number.isFinite(item.quantity) && item.quantity > 0 ? item.quantity : 1;
  const boardFeet =
    typeof item.boardFeet === 'number' && item.boardFeet > 0 ? item.boardFeet : parseBoardFeet(dimensions);
  return {
    id: item.id || `timber-${item.name || 'line'}`,
    structureId: item.structureId || '',
    structureName: item.structureName || '',
    name: item.name || '',
    species: item.species || item.material || '',
    grade: item.grade || '',
    dimensions,
    moistureContent: item.moistureContent ?? '',
    quantity,
    boardFeet,
    unitCost,
    total: typeof item.total === 'number' && Number.isFinite(item.total) ? item.total : unitCost * quantity,
    sourceDocumentId: item.documentId,
  };
}

export function timberListItemsFromAnalysis(analysis: LeadBiddingAnalysis | null | undefined): TimberListItem[] {
  const extraction = analysis?.timberExtraction;
  if (!extraction) return [];

  const fromStructures = (extraction.structures || []).flatMap(structure =>
    (structure.timberList || []).map(row => ({
      ...row,
      structureId: row.structureId || structure.id,
      structureName: row.structureName || structure.name,
      dimensions: standardizeTimberDimensions(row.dimensions || ''),
      boardFeet: row.boardFeet || parseBoardFeet(row.dimensions || ''),
    })),
  );
  if (fromStructures.length > 0) return fromStructures;

  return (extraction.items || [])
    .map(timberListItemFromBiddingItem)
    .filter(row => row.name || row.dimensions);
}

export function takeoffStructuresFromAnalysis(
  analysis: LeadBiddingAnalysis | null | undefined,
): BiddingTakeoffStructure[] {
  const extraction = analysis?.timberExtraction;
  if (!extraction) return [];
  if (extraction.structures && extraction.structures.length > 0) {
    return extraction.structures.map(s => ({
      id: s.id,
      name: s.name,
      timberList: (s.timberList || []).map(row => ({
        ...row,
        structureId: row.structureId || s.id,
        structureName: row.structureName || s.name,
        dimensions: standardizeTimberDimensions(row.dimensions || ''),
      })),
    }));
  }

  const byStructure = new Map<string, BiddingTakeoffStructure>();
  for (const item of extraction.items || []) {
    const listItem = timberListItemFromBiddingItem(item);
    const id = item.structureId || listItem.structureId || 'unassigned';
    const name = item.structureName || listItem.structureName || 'Take-off';
    if (!byStructure.has(id)) {
      byStructure.set(id, { id, name, timberList: [] });
    }
    byStructure.get(id)!.timberList.push({ ...listItem, structureId: id, structureName: name });
  }
  return Array.from(byStructure.values());
}

export function draftStructuresFromTakeoff(
  structures: BiddingTakeoffStructure[],
  meta: { dealId: string; leadId: string; userId: string; userName: string },
): PdfTakeoffStructure[] {
  return structures
    .filter(s => s.id && s.id !== 'unassigned')
    .map(s => ({
      id: s.id,
      dealId: meta.dealId,
      leadId: meta.leadId,
      name: s.name,
      userId: meta.userId,
      userName: meta.userName,
      createdAt: new Date(),
    }));
}

/** Flatten structures → bidding items so the Analyzed chip + training table still work. */
export function biddingItemsFromStructures(
  extraction: BiddingTimberExtraction,
): BiddingTimberItem[] {
  if (extraction.items && extraction.items.length > 0) return extraction.items;
  const items: BiddingTimberItem[] = [];
  for (const structure of extraction.structures || []) {
    for (const row of structure.timberList || []) {
      const parts = (row.dimensions || '').match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
      items.push({
        id: row.id,
        name: row.name,
        quantity: row.quantity,
        width: parts ? parseFloat(parts[2]) : 0,
        height: parts ? parseFloat(parts[1]) : 0,
        length: parts ? `${parts[3]}'` : '',
        dimensions: row.dimensions,
        species: row.species,
        grade: row.grade,
        moistureContent: row.moistureContent,
        structureId: row.structureId || structure.id,
        structureName: row.structureName || structure.name,
        boardFeet: row.boardFeet,
        unitCost: row.unitCost,
        total: row.total,
        documentId: row.sourceDocumentId,
        material: row.species,
      });
    }
  }
  return items;
}
